import { useMutation } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useErrorBanner } from "@/providers/ErrorBannerProvider";
import { WorkingLocation } from "@/types";
import { UPDATE_SUBMITTED_APPLICATION_MUTATION } from "../mutations";
import type { ApplicationEmploymentType } from "../mutations";
import type { ApplicationEmploymentDetails } from "../queries";
import { MEMBERSHIP_APPLICATION_QUERY } from "../queries";
import UpdateEmploymentClassificationAndTitleModal from "./UpdateEmploymentClassificationAndTitleModal";
import type { UpdateEmploymentClassificationAndTitleValues } from "./UpdateEmploymentClassificationAndTitleModal";

interface Props {
  open: boolean;
  onClose: () => void;
  applicationId: string;
  employmentType: ApplicationEmploymentType;
  employer: string | null;
  facilityId: string | null;
  jobTitle: string | null;
  jobClassification: string | null;
  workingLocation: ApplicationEmploymentDetails["workingLocation"];
  startDate: string | null;
}

export default function UpdateEmploymentClassificationModal({
  open,
  onClose,
  applicationId,
  employmentType,
  employer,
  facilityId,
  jobTitle,
  jobClassification,
  workingLocation,
  startDate
}: Props) {
  const { setError, clearError } = useErrorBanner();

  const [updateApplication, { loading }] = useMutation(UPDATE_SUBMITTED_APPLICATION_MUTATION, {
    refetchQueries: [{ query: MEMBERSHIP_APPLICATION_QUERY, variables: { id: applicationId } }],
    onCompleted: (data) => {
      if (data.updateSubmittedApplication.success) {
        clearError();
        showSnackbar(data.updateSubmittedApplication.message);
        onClose();
      } else {
        setError(data.updateSubmittedApplication.message);
      }
    },
    onError: (error) => {
      setError(error.message);
    }
  });

  const handleSave = async (values: UpdateEmploymentClassificationAndTitleValues) => {
    // Omit blank facilityId/startDate so backend key? checks leave existing nulls alone.
    // Only send those keys when the reviewer sets a non-empty value.
    const facilityId = values.facilityId?.trim() || null;
    const startDate = values.startDate?.trim() || null;

    await updateApplication({
      variables: {
        input: {
          id: applicationId,
          employmentClassification: {
            employmentType,
            jobClassification: values.jobClassification,
            jobTitle: values.jobTitle,
            workingLocation: values.workingLocation,
            ...(facilityId ? { facilityId } : {}),
            ...(startDate ? { startDate } : {})
          }
        }
      }
    });
  };

  return (
    <UpdateEmploymentClassificationAndTitleModal
      open={open}
      onClose={onClose}
      programType="associate"
      employer={employer}
      currentFacilityId={facilityId}
      currentJobTitle={jobTitle}
      currentJobClassification={jobClassification}
      currentWorkingLocation={
        workingLocation === "ON_SITE"
          ? WorkingLocation.ON_SITE
          : workingLocation === "REMOTE"
            ? WorkingLocation.REMOTE
            : null
      }
      currentStartDate={startDate}
      saving={loading}
      onSave={handleSave}
    />
  );
}
